import { useCallback, useMemo } from 'react';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TSegment } from '@/features/segments/types';

type TUseBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState = {
  data: TSegment[];
};

// Moving to another segment only has to change the selection and drop the box
// drawn over the previous one: presence follows the active segment on its own
// (see `usePresenceLease`).
export function useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState({
  data: items,
}: TUseBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState) {
  const { activeSegment, setActiveSegment, setVisibleBox } = useBroadcastScheduleProgram();

  const goToSegment = useCallback(
    (segment: TSegment | undefined) => {
      if (!segment) return;
      setVisibleBox(null);
      setActiveSegment(segment);
    },
    [setActiveSegment, setVisibleBox]
  );

  const currentIndex = useMemo(
    () => items.findIndex(item => item.id === activeSegment?.id),
    [activeSegment, items]
  );

  const handleNextItem = useCallback(() => {
    if (!activeSegment || currentIndex === -1 || currentIndex === items.length - 1) return;
    goToSegment(items[currentIndex + 1]);
  }, [activeSegment, currentIndex, items, goToSegment]);

  const handlePreviousItem = useCallback(() => {
    if (!activeSegment || currentIndex <= 0) return;
    goToSegment(items[currentIndex - 1]);
  }, [activeSegment, currentIndex, items, goToSegment]);

  const hasNextItem = useMemo(() => {
    if (!activeSegment) return false;
    return currentIndex !== items.length - 1;
  }, [activeSegment, currentIndex, items]);

  const hasPreviousItem = useMemo(() => {
    if (!activeSegment) return false;
    return currentIndex > 0;
  }, [activeSegment, currentIndex]);

  const handleGoXItem = useCallback(
    (index: number) => goToSegment(items[index]),
    [items, goToSegment]
  );

  const handleGoLast = useCallback(
    () => goToSegment(items[items.length - 1]),
    [items, goToSegment]
  );

  const handleGoFirst = useCallback(() => goToSegment(items[0]), [items, goToSegment]);

  return {
    handleNextItem,
    hasNextItem,
    handlePreviousItem,
    hasPreviousItem,
    handleGoXItem,
    handleGoLast,
    handleGoFirst,
  };
}
